import React, { Fragment, useEffect } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { defaultFilters, filterOptions } from './config'
import { ApiResponse } from '../../typings/ApiResponse'
import { useApi } from '../../hooks/useApi'
import { Status } from '../../hooks/useFetchState'
import { usePagination } from '../../hooks/usePagination'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { TableColumn } from '../../components/_EOS/GenericTable/types'
import { useModal } from '../../hooks/useModal'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { YesNoBadge } from '../../components/YesNoBadge'
import AddHomeWorkIcon from '@mui/icons-material/AddHomeWork'
import { Modal } from '../../components/_EOS/Modal/Modal'
import Assignment, { AssignmentRef } from './modal/Assignment'
import { getUserAdminWMS } from './config/api'

interface UserPosition {
  userId: number
  positionId: number
  zoneId: number
  positionName: string
}

interface UserAdmin {
  userId: number
  email: string
  userAlias: string
  active: boolean
  userPositions: UserPosition[]
}

const columns: TableColumn<UserAdmin>[] = [
  { key: 'userAlias', title: 'Nombre' },
  { key: 'email', title: 'Email' },
  {
    key: 'userPositions',
    title: 'Posición',
    render: (value) => (value as UserPosition[])?.[0]?.positionName ?? '-'
  },
  {
    key: 'active',
    title: 'Activo',
    render: (value) => <YesNoBadge value={value as boolean} />
  }
]

interface UserPageProps {}

export const UserAdminPage: React.FC<UserPageProps> = () => {
  const [selectedFilters, setSelectedFilters] = React.useState<Filter[]>(defaultFilters)
  const { state, callApi } = useApi<ApiResponse<UserAdmin>>()
  const pagination = usePagination(10)
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const editModal = useModal<{ user: UserAdmin }>(``)
  const assignmentRef = React.useRef<AssignmentRef>(null)
  const [saving, setSaving] = React.useState(false)

  const requestUsers = (filters?: Filter[], page?: number, pageSize?: number) => {
    const emailFilter = filters?.find((f) => f.key === 'email')?.value as string | undefined
    const nameFilter = filters?.find((f) => f.key === 'name')?.value as string | undefined
    const warehouseIdFilter = filters?.find((f) => f.key === 'warehouseId')?.value as number | undefined
    const zoneIdFilter = filters?.find((f) => f.key === 'zoneId')?.value as number | undefined
    const activeFilter = filters?.find((f) => f.key === 'active')?.value as string | undefined
    const positionCodeFilter = filters?.find((f) => f.key === 'positionCode')?.value as string | undefined
    const userIdFilter = filters?.find((f) => f.key === 'userId')?.value as string | undefined
    callApi(getUserAdminWMS(page, pageSize, emailFilter, nameFilter, warehouseIdFilter, zoneIdFilter, activeFilter, positionCodeFilter, userIdFilter)).catch(console.log)
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestUsers(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }


  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)
    requestUsers(selectedFilters, 0, pagination.pageSize)
  }, [])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          options={filterOptions}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={requestUsers}
          includeSearchOnClean={true}
          includeAddButton={true}
        />

        <GenericTableV2<UserAdmin>
          data={state.data?.items || []}
          columns={columns}
          keyField='userId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePaginationChange
          }}
          actions={[
            {
              key: 'edit',
              label: 'Gestionar Zonas',
              icon: <AddHomeWorkIcon />,
              onClick: (row) => editModal.openModal('sm', { user: row }),
              disabled: (row) => !row.active
            }
          ]}
        />
      </StandardPage>

      <Modal
        open={editModal.isOpen}
        title='Configuración'
        onClose={editModal.closeModal}
        maxWidth='md'
        loading={saving}
        cancelButton={{ title: 'Cerrar', action: editModal.closeModal }}
        successButton={{ title: 'Guardar', action: () => assignmentRef.current?.save() }}
      >
        <Assignment
          ref={assignmentRef}
          user={editModal.extendedProps?.user}
          onLoadingChange={setSaving}
          onSaveSuccess={() => openSnackBar('Asignación de Zonas guardadas correctamente', true)}
        />
      </Modal>

      <SnackBar
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
        message={message}
      />
    </Fragment>
  )
}
